import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="identity--bulk-roles" (CONEK-7553)
// Row selection + bulk role bar on the admin users list. Mirrors
// catalog--bulk-operations: desktop and mobile render a checkbox per user with
// the same value, so selection is de-duplicated by value. The chosen role's
// option carries the levels its permissions allow (data-levels); the server
// validates the same rule, this only keeps the bar honest.
export default class extends Controller {
  static targets = [
    "selectAll",
    "checkbox",
    "actionBar",
    "count",
    "idsContainer",
    "role",
    "level",
    "branches",
    "levelHint",
    "modal",
  ]

  static values = {
    selectedText: { type: String, default: "%{count} selected" },
  }

  toggle(event) {
    this.checkboxTargets.forEach((cb) => {
      if (cb !== event.target && cb.value === event.target.value) {
        cb.checked = event.target.checked
      }
    })
    this.updateState()
  }

  toggleAll() {
    const checked = this.selectAllTarget.checked
    this.checkboxTargets.forEach((cb) => (cb.checked = checked))
    this.updateState()
  }

  // The preview turbo_stream renders a closed Ui::ModalComponent; open it
  // through ui--modal so it gets showModal(), the backdrop and focus handling.
  // Deferred a frame: ui--modal may connect after this callback fires.
  modalTargetConnected(element) {
    requestAnimationFrame(() => {
      const modal = this.application.getControllerForElementAndIdentifier(
        element,
        "ui--modal",
      )
      modal?.open()
    })
  }

  // The table is a turbo-frame: paging swaps the rows, so drop what left.
  checkboxTargetDisconnected() {
    this.updateState()
  }

  updateState() {
    if (!this.hasActionBarTarget) return

    const selected = this.selectedValues()
    const total = new Set(this.checkboxTargets.map((cb) => cb.value)).size

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = total > 0 && selected.length === total
      this.selectAllTarget.indeterminate =
        selected.length > 0 && selected.length < total
    }

    this.actionBarTarget.classList.toggle("hidden", selected.length === 0)
    this.countTarget.textContent = this.selectedTextValue.replace(
      "%{count}",
      selected.length,
    )

    this.idsContainerTarget.innerHTML = ""
    selected.forEach((value) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "bulk[user_ids][]"
      input.value = value
      this.idsContainerTarget.appendChild(input)
    })
  }

  roleChanged() {
    const levels = this.allowedLevels()
    const hint = this.levelHintTarget

    hint.textContent = levels.length
      ? hint.dataset.levelsText.replace(
          "%{levels}",
          levels.map((l) => hint.dataset[`${l}Text`]).join(", "),
        )
      : ""
    this.levelChanged()
  }

  // Keeps the level on one the chosen role allows, whether the role or the
  // level was the last thing changed.
  levelChanged() {
    const level = this.selectIn(this.levelTarget)
    const levels = this.allowedLevels()
    if (levels.length && !levels.includes(level.value)) {
      // Go through Tom Select when it enhances the select, so its UI follows.
      if (level.tomselect) {
        level.tomselect.setValue(levels[0], true)
      } else {
        level.value = levels[0]
      }
    }
    this.branchesTarget.classList.toggle("hidden", level.value !== "branch")
  }

  allowedLevels() {
    const option = this.selectIn(this.roleTarget).selectedOptions[0]
    return (option?.dataset.levels || "").split(" ").filter(Boolean)
  }

  selectedValues() {
    return [
      ...new Set(
        this.checkboxTargets.filter((cb) => cb.checked).map((cb) => cb.value),
      ),
    ]
  }

  // Ui::SelectComponent puts data attributes on its wrapper, not the <select>.
  selectIn(element) {
    return element.matches("select") ? element : element.querySelector("select")
  }
}
